import { AppDialog } from "core/Controls/AppDialog";
import { ContextualPopup, ContextualPopupAlignment, ContextualPopupBehavior, ContextualPopupOrientation } from "shared/Components/ContextualPopup";
import { HeaderFlyout } from "core/Controls/HeaderFlyout";
import { StylizedTabset } from "core/Controls/StylizedTabset";
import { TaskMessage } from "core/Controls/TaskMessage";
import { TogglerTabset } from "core/Controls/Toggler";
import { TableSorter } from "core/TableSorter";
import { $getAppPinService, $windowLayout } from "core/services";
import { GifPauser } from "shared/ReducedMotion/GifPauser";
import {
	waitForDocumentReady,
	isHTMLElement,
	isHTMLElementOfType,
	mustBeHTMLElement,
	mustBeHTMLElementOfType
} from "silver";
import {
	$focusControl,
	$route,
	$dialog,
	$setLanguageService,
	$router,
	$httpClient,
	$client,
	$getLanguageLoader, $getAuthListener, $inputMethodDetector
} from "shared/services";
import { isEnvUserAuthorized, MyonEnv } from "shared/Api/MyonEnv";
import { Role } from "shared/Api/Role";

export default async function global(
	env: MyonEnv,
) {
	const langLoader = $getLanguageLoader(env);
	$inputMethodDetector.listen();

	await waitForDocumentReady();

	const skipToContentButton = document.getElementById("skip-to-content");
	if (skipToContentButton) {
		skipToContentButton.addEventListener("click", function(e) {
			e.preventDefault();
			$focusControl.focusArea(mustBeHTMLElement(document.querySelector(".layout_body")));
		});
	}

	if ([ "login\\index", "system_check" ].indexOf($route.path) === -1) {
		$getAuthListener(env).listenForAuth();
	}

	document.body.addEventListener("click", ({ target }) => {
		const anchor = isHTMLElement(target) ? target.closest("a[href]") : null;
		if (!isHTMLElementOfType(anchor, "a")) {
			return;
		}
		if (anchor.host && anchor.host != window.location.host) {
			const url = "/api/ping.externalreq?url="+encodeURIComponent(anchor.href);
			anchor.setAttribute("ping", url);
		}
	});

	document.body.addEventListener("submit", (e) => {
		const form = isHTMLElement(e.target) ? e.target.closest("form.js-submit-once") : null;
		if (! isHTMLElementOfType(form, "form")) {
			return;
		}
		// subsequent submits are prevented
		form.addEventListener("submit", (e) => {
			e.preventDefault();
			e.stopPropagation();
		});
	});

	if (isEnvUserAuthorized(env) && env.auth.roleId !== Role.STUDENT) {
		import("admin/services").then(s => {
			s.$getBookBag(env);
		});
	}

	const languagePicker = document.getElementById("language-picker");
	if (isHTMLElementOfType(languagePicker, "select")) {
		languagePicker.addEventListener("change", function() {
			$setLanguageService.setLanguage(languagePicker.value);
		});
	}



	Array.from(document.querySelectorAll("[data-tooltip],[data-tooltip-id]"))
		.filter(isHTMLElement)
		.forEach(function(el) {
			let tooltip: ContextualPopup;
			if (el.dataset.tooltipId) {
				const behavior = el.dataset.tooltipBehavior === "click" ? ContextualPopupBehavior.CLICK : ContextualPopupBehavior.HOVER;
				tooltip = new ContextualPopup(mustBeHTMLElement(document.getElementById(el.dataset.tooltipId)));
				tooltip.setContext(el, behavior);
			} else {
				tooltip = ContextualPopup.makeFromHTML(el, el.dataset.tooltip ?? "");
			}
			tooltip.forcedOrientation = el.dataset.tooltipOrientation as ContextualPopupOrientation | undefined;
			tooltip.forcedAlignment = el.dataset.tooltipAlignment as ContextualPopupAlignment | undefined;
		});

	const msgEnvelope = document.getElementById("msg-envelope");
	if (msgEnvelope) {
		msgEnvelope.addEventListener("click", function(e) {
			e.preventDefault();

			const taskMessage = new TaskMessage($dialog, $router, $httpClient, $client);

			taskMessage.message(1, msgEnvelope);
		});
	}

	HeaderFlyout.initDocument(document);

	const appButton = document.getElementById("appButton");
	if (isHTMLElementOfType(appButton, "button") && isEnvUserAuthorized(env)) {
		(new AppDialog(
			appButton,
			env.auth.username,
			env.auth.buildingId,
			$getAppPinService(env),
			langLoader,
			$dialog,
			env.assetCdn,
		))
	}

	Array.from(document.querySelectorAll("[data-demo-notice][data-demo-notice-title]"))
		.filter(isHTMLElement)
		.forEach(function(el) {
			const popup = ContextualPopup.makeFromTitleAndText(
				el.dataset.demoNoticeTitle ?? "",
				el.dataset.demoNotice ?? "",
				"-demo",
				"-infoNotice"
			);
			popup.setContext(el, ContextualPopupBehavior.HOVER);
		});

	Array.from(document.querySelectorAll("table[data-auto-tablesorter]"))
		.map(t => new TableSorter(mustBeHTMLElementOfType(t, "table")));

	Array.from(document.querySelectorAll("[data-toggler-tabset]"))
		.map(el => new TogglerTabset(mustBeHTMLElement(el)));

	// All from `jsTabs.html`
	Array.from(document.querySelectorAll("[data-tabset]")).forEach(function(el) {
		const tabset = StylizedTabset.makeFromDomElement(mustBeHTMLElement(el));
		function handleTabChange() {
			const activeTab = tabset.activeTab;
			tabset.forEachTab(function(tab) {
				Array.from(tab.tab.querySelectorAll(".button.-toggle")).forEach(function(button) {
					button.classList.toggle("-checked", tab === activeTab);
				});
			});
			$windowLayout.doUpdate();
		}

		tabset.onTabActivate(handleTabChange);
		handleTabChange();
	});

	const gifPauser = new GifPauser();
	gifPauser.update();
}
